/* ═══════════════════════════════════════════════════════════════════════════
   Hystride core

   Replaces the prototype's site.js + chrome.js. chrome.js does not survive at
   all — it becomes Liquid. site.js splits into the modules below.

   Two rules this file exists to enforce:

   1. NOTHING IS GLOBAL. The prototype declares `card()` twice — site.js:424 for
      the chat product card and page-collection.js:137 for a collection grid
      card — both at top level. page-collection.js loads second, so on the
      collection page the chat's product card silently throws. Everything here
      lives on one namespace.

   2. SECTIONS INITIALISE THEMSELVES. The prototype calls initLate() once, from
      the middle of a page script. In a theme that cannot hold: Liquid renders
      server-side, and the theme editor loads, reloads and reorders individual
      sections at runtime. Every behaviour registers here and is bound per
      section on shopify:section:load, unbound on shopify:section:unload.
      Without this the rail arrows and the colour picker stop working the first
      time Ed edits a section — which is the first thing he will do.

   Budget: Shopify's theme JS guidance is =16 KB minified, no framework.
   ═══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  document.documentElement.classList.remove('no-js');

  /** Respect the visitor's motion preference. Queried live, not cached — the OS
   *  setting can change while the page is open. */
  const reduceMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const Hystride = (window.Hystride = window.Hystride || {});
  Hystride.reduceMotion = reduceMotion;

  /* ─────────────────────────────────────────────────────────────────────────
     Section registry

     register(name, { mount, unmount }) — mount receives the section root
     element. Anything mount returns is passed back to unmount, which is where
     intervals, observers and document-level listeners get torn down.
     ───────────────────────────────────────────────────────────────────────── */
  const registry = new Map();
  const mounted = new WeakMap();

  function mount(root) {
    if (!root || mounted.has(root)) return;
    const name = root.dataset.hysSection;
    const mod = registry.get(name);
    if (!mod) return;
    try {
      mounted.set(root, mod.mount(root) || {});
    } catch (err) {
      // A section that throws must not take the rest of the page with it —
      // that is precisely how the prototype loses body.ready and leaves the
      // hero with no text.
      console.error(`[hystride] ${name} failed to mount`, err);
      mounted.set(root, {});
    }
  }

  function unmount(root) {
    if (!root || !mounted.has(root)) return;
    const mod = registry.get(root.dataset.hysSection);
    const state = mounted.get(root);
    mounted.delete(root);
    if (mod && mod.unmount) {
      try {
        mod.unmount(root, state);
      } catch (err) {
        console.error(`[hystride] ${root.dataset.hysSection} failed to unmount`, err);
      }
    }
  }

  const roots = (scope) =>
    Array.from((scope || document).querySelectorAll('[data-hys-section]'));

  Hystride.register = function (name, mod) {
    registry.set(name, mod);
    // A section may register after DOM ready (deferred module, editor reload),
    // so sweep for instances that are already in the document.
    roots().forEach((r) => r.dataset.hysSection === name && mount(r));
  };

  const ready = (fn) =>
    document.readyState === 'loading'
      ? document.addEventListener('DOMContentLoaded', fn, { once: true })
      : fn();

  ready(() => roots().forEach(mount));

  document.addEventListener('shopify:section:load', (e) => roots(e.target).forEach(mount));
  document.addEventListener('shopify:section:unload', (e) => roots(e.target).forEach(unmount));
  // Reordering re-parents the section without firing load/unload in every
  // Shopify version, so remeasure anything position-dependent.
  document.addEventListener('shopify:section:reorder', () => measureChrome());

  /* ─────────────────────────────────────────────────────────────────────────
     Chrome height

     The prototype hardcodes 92px in five places: site.css twice and the scroll
     arithmetic in page-home.js three times. One measurement now, published as
     --chrome-h on <html> so both CSS and JS read the same number.

     It must go on <html>, not <body>: Horizon writes --header-group-height onto
     body, and html cannot read a property set on its own descendant.
     ───────────────────────────────────────────────────────────────────────── */
  let chromeH = 92;

  function measureChrome() {
    const group = document.getElementById('header-group');
    if (!group) return;
    const h = Math.round(group.getBoundingClientRect().height);
    if (!h || h === chromeH) return;
    chromeH = h;
    document.documentElement.style.setProperty('--chrome-h', h + 'px');
    document.dispatchEvent(new CustomEvent('hystride:chrome-height', { detail: { height: h } }));
  }

  Hystride.chromeHeight = () => chromeH;

  /* The nav's own height, separately from the group's.

     They are not the same number and cannot substitute for each other: the
     announcement bar scrolls away while the nav stays stuck at the top, so
     anything sticking UNDER the nav has to clear the nav alone. The filter bar
     asks for --navh; nothing published it, so it fell back to a hardcoded 58px
     and drifted against the real nav as the announcement bar scrolled past. */
  let navH = 58;

  function measureNav() {
    const nav = document.querySelector('.nav');
    if (!nav) return;
    const h = Math.round(nav.getBoundingClientRect().height);
    if (!h || h === navH) return;
    navH = h;
    document.documentElement.style.setProperty('--navh', h + 'px');
  }

  Hystride.navHeight = () => navH;

  ready(() => {
    measureChrome();
    measureNav();
    const group = document.getElementById('header-group');
    if (group && 'ResizeObserver' in window) {
      new ResizeObserver(measureChrome).observe(group);
    }
    const nav = document.querySelector('.nav');
    if (nav && 'ResizeObserver' in window) {
      new ResizeObserver(measureNav).observe(nav);
    }
    window.addEventListener('resize', measureChrome, { passive: true });
    window.addEventListener('resize', measureNav, { passive: true });
    // The announcement bar rotates and can change height as it does.
    document.addEventListener('shopify:section:load', measureChrome);
    document.addEventListener('shopify:section:load', measureNav);
  });

  /* ─────────────────────────────────────────────────────────────────────────
     Reveal animation

     Every .rv element starts at opacity:0 and gets .in when it enters view.
     The no-JS fallback lives in hystride-base.css behind html.no-js; this is
     the belt to that braces. Under reduced motion everything is revealed
     immediately rather than animated.
     ───────────────────────────────────────────────────────────────────────── */
  let revealObserver = null;

  function reveal(scope) {
    const els = Array.from((scope || document).querySelectorAll('.rv:not(.in)'));
    if (!els.length) return;

    if (reduceMotion() || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('in'));
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries) =>
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('in');
            revealObserver.unobserve(entry.target);
          }),
        { threshold: 0.1, rootMargin: '0px 0px -6% 0px' }
      );
    }
    els.forEach((el) => revealObserver.observe(el));
  }

  Hystride.reveal = reveal;
  ready(() => {
    reveal();
    document.body.classList.add('ready');
  });
  document.addEventListener('shopify:section:load', (e) => reveal(e.target));

  /* ─────────────────────────────────────────────────────────────────────────
     Rails — the shared horizontal carousel

     The prototype computes its scroll step as
       rail.firstElementChild.getBoundingClientRect().width + 12
     where 12 is the gap. But the gap is clamp(8px,1.1vw,16px), so the two drift
     apart at both ends of the range and the scroll lands off the snap point.
     Scrolling to a child's offsetLeft sidesteps the arithmetic entirely.

     sync() must also re-run after any re-render (the kit filters replace the
     rail's innerHTML) and on resize, neither of which the prototype does.
     ───────────────────────────────────────────────────────────────────────── */
  function initRail(nav, rail) {
    if (!nav || !rail) return null;

    const prev = nav.querySelector('[data-rail-prev]');
    const next = nav.querySelector('[data-rail-next]');

    const sync = () => {
      const max = rail.scrollWidth - rail.clientWidth - 1;
      const atStart = rail.scrollLeft <= 1;
      const atEnd = rail.scrollLeft >= max;
      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;
      // Hide the whole control when everything already fits.
      nav.hidden = max <= 1;
    };

    const step = (dir) => {
      const kids = Array.from(rail.children);
      if (!kids.length) return;
      const width = kids[0].getBoundingClientRect().width;
      const target = rail.scrollLeft + dir * width * 2;
      // Land on a child, so scroll-snap has nothing to correct.
      const dest = kids.reduce(
        (best, kid) =>
          Math.abs(kid.offsetLeft - target) < Math.abs(best - target) ? kid.offsetLeft : best,
        kids[0].offsetLeft
      );
      rail.scrollTo({ left: dest, behavior: reduceMotion() ? 'auto' : 'smooth' });
    };

    const onPrev = () => step(-1);
    const onNext = () => step(1);

    if (prev) prev.addEventListener('click', onPrev);
    if (next) next.addEventListener('click', onNext);
    rail.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync, { passive: true });
    requestAnimationFrame(sync);

    return {
      sync,
      destroy() {
        if (prev) prev.removeEventListener('click', onPrev);
        if (next) next.removeEventListener('click', onNext);
        rail.removeEventListener('scroll', sync);
        window.removeEventListener('resize', sync);
      },
    };
  }

  Hystride.initRail = initRail;

  /* ─────────────────────────────────────────────────────────────────────────
     Hover images on product cards

     The second card image only ever appears on a device with a real pointer.
     The prototype loads it everywhere, so every touch visitor downloads a
     740px image per card that they can never see — on a 24-product collection
     page that is 24 wasted requests.

     So the markup ships without a src, and we fill it in only where hover
     actually exists, and only once the card is near the viewport.
     ───────────────────────────────────────────────────────────────────────── */
  ready(() => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const load = (img) => {
      if (img.src) return;
      if (img.dataset.sizes) img.sizes = img.dataset.sizes;
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
    };

    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('img[data-hys-hover]').forEach(load);
      return;
    }

    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          load(entry.target);
          io.unobserve(entry.target);
        }),
      { rootMargin: '200px' }
    );

    const observe = (scope) =>
      (scope || document).querySelectorAll('img[data-hys-hover]').forEach((img) => io.observe(img));

    observe();
    document.addEventListener('shopify:section:load', (e) => observe(e.target));
  });

  /** Wire every .rnav/rail pair inside a section root. Scoped to the section so
   *  IDs never have to be globally unique — the prototype's data-rail="rangeRail"
   *  + getElementById does not survive two instances of the same section. */
  Hystride.initRails = function (root) {
    return Array.from(root.querySelectorAll('[data-rail-nav]'))
      .map((nav) => {
        const rail = root.querySelector(
          `[data-rail="${nav.getAttribute('data-rail-nav')}"]`
        );
        return initRail(nav, rail);
      })
      .filter(Boolean);
  };
})();
